import { useState, useEffect } from "react"

import {
  getAuth,
  OAuthProvider,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailLink,
  sendSignInLinkToEmail,
  browserPopupRedirectResolver,
} from "firebase/auth"
import { useForm } from "react-hook-form"
import { initializeApp } from "firebase/app"
import { getDatabase } from "firebase/database"

import { Helpers } from "@flexday/common-react"
import Input from "../reusable-components/input"
import checkIcon from "../assets/icons/check.svg"
import { createUser } from "../signup/create-user"
import Button from "../reusable-components/button"

import SignInWithAppleImage from "../assets/images/apple_si.png"
import SignInWithGoogleImage from "../assets/images/google_si.svg"

const Login = ({ loginUrl, onError, onSuccess }) => {
  const { getFirebaseConfig } = Helpers
  const [user, setUser] = useState(null)
  const [isLinkSent, setIsLinkSet] = useState(false)
  const queryParameters = new URLSearchParams(window.location.search)
  const oobCode = queryParameters.get("oobCode")
  const redirectUrl = queryParameters.get("redirectUrl")

  const {
    watch,
    reset,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm()

  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })
  const app = initializeApp(firebaseConfig)
  const auth = getAuth(app)
  const database = getDatabase(app)

  auth.onAuthStateChanged((user) => {
    setUser(user)
  })

  useEffect(() => {
    if (oobCode) {
      onSignIn()
    }
  }, [])

  const onSignIn = async () => {
    try {
      const emailLink = window.location.href
      const {
        firstName = "",
        lastName = "",
        email,
      } = JSON.parse(localStorage.getItem("userInfo"))
      const { user } = await signInWithEmailLink(auth, email, emailLink)

      if (firstName || lastName) {
        await updateProfile(user, {
          displayName: `${firstName} ${lastName}`.trim(),
        })

        localStorage.removeItem("userInfo")
      }

      await createUser(database, user, { firstName, lastName })

      window.history.pushState({}, "", emailLink.split("?")[0])

      onSuccess(user, redirectUrl)
    } catch (error) {
      onError(error)
    }
  }

  const onSendLoginLink = async ({ email }) => {
    setIsLinkSet(false)

    const settings = {
      url: redirectUrl ? `${loginUrl}?redirectUrl=${redirectUrl}` : loginUrl,
      handleCodeInApp: true,
    }

    try {
      const auth = getAuth(app)
      localStorage.setItem(
        "userInfo",
        JSON.stringify({ email, firstName: "", lastName: "" })
      )
      await sendSignInLinkToEmail(auth, email, settings)

      reset({ email })
      setIsLinkSet(true)
    } catch (error) {
      onError(error)
    }
  }

  const onSignInWithGoogle = async () => {
    try {
      const auth = getAuth(app)
      const provider = new GoogleAuthProvider()

      const { user } = await signInWithPopup(
        auth,
        provider,
        browserPopupRedirectResolver
      )

      await createUser(database, user)
      onSuccess(user, redirectUrl)
    } catch (error) {
      onError(error)
    }
  }

  const onSignInWithApple = async () => {
    try {
      const auth = getAuth(app)
      const provider = new OAuthProvider("apple.com")

      const { user } = await signInWithPopup(auth, provider)

      await createUser(database, user)
      onSuccess(user, redirectUrl)
    } catch (error) {
      onError(error)
    }
  }

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={handleSubmit(onSendLoginLink)}>
      <div className="relative max-w-sm">
        <Input
          required
          name="email"
          type="email"
          className="w-full"
          placeholder="Email"
          register={register}
        />
        {isLinkSent && (
          <img
            alt="success"
            src={checkIcon}
            className="absolute right-[-34px] top-2.5"
          />
        )}
      </div>
      <div className="flex flex-col justify-center gap-2 w-48">
        <Button
          disabled={!watch("email") || !isDirty}
          type="submit"
          title="Login"
          className="h-14 font-bold bg-purple-400 hover:bg-purple-500
           disabled:bg-purple-300 disabled:hover:bg-purple-300
           disabled:cursor-not-allowed transition-colors
           duration-300 ease-in-out"
        />
        <p className="text-xs text-center italic text-black font-light">
          Send a link to email to login.
        </p>
      </div>
      <div className="flex flex-col gap-8 mt-10">
        <p>or sign in with:</p>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button type="button">
            <img
              src={SignInWithAppleImage}
              onClick={onSignInWithApple}
              className="h-10"
            />
          </button>
          <button type="button">
            <img
              src={SignInWithGoogleImage}
              onClick={onSignInWithGoogle}
              className="h-10"
            />
          </button>
        </div>
      </div>
    </form>
  )
}

export default Login
